import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";

const AppShellContext = createContext(null);

export function AppShellProvider({ user, role, children }) {
  // We rename the base setter to _setActiveTab so we can intercept it
  const [activeTab, _setActiveTab] = useState("home");
  const [exploreChip, setExploreChip] = useState("stories");
  
  // ✅ ADDED: State to manage the active tab inside ConnectScreen
  const [connectTab, setConnectTab] = useState("feed");
  
  // A trigger to signal when an active tab is clicked again
  const [resetTrigger, setResetTrigger] = useState(0); 

  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem("hh_dark") === "true"; } catch { return false; }
  });

  // The Interceptor. If clicking the same tab, fire the reset trigger.
  const setActiveTab = useCallback((newTab) => {
    _setActiveTab(prev => {
      if (prev === newTab) {
        setResetTrigger(t => t + 1); // Fire the reset!
        return prev;
      }
      return newTab;
    });
  }, []);

  const toggleDark = useCallback(() => {
    setIsDark(prev => {
      const next = !prev;
      try { localStorage.setItem("hh_dark", String(next)); } catch {}
      return next;
    });
  }, []);

  const themeClass = isDark ? "dark-theme" : "light-theme";
  const themeRef = useRef(themeClass);
  themeRef.current = themeClass;

  useEffect(() => {
    document.body.className = themeClass;
    localStorage.setItem("theme", themeClass);

    const observer = new MutationObserver(() => {
      if (document.body.className !== themeRef.current) {
        document.body.className = themeRef.current;
      }
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, [themeClass]);

  return (
    <AppShellContext.Provider value={{ 
      activeTab, 
      setActiveTab, 
      exploreChip,      
      setExploreChip,
      connectTab,      // ✅ Exported new state
      setConnectTab,   // ✅ Exported setter
      resetTrigger, 
      user, 
      role, 
      isDark, 
      toggleDark 
    }}>
      {children}
    </AppShellContext.Provider>
  );
}

export function useAppShell() {
  const ctx = useContext(AppShellContext);
  if (!ctx) throw new Error("useAppShell must be used inside AppShellProvider");
  return ctx;
}